import { useEffect, useRef, useState, type ComponentProps } from "react";
import useSWRMutation from "swr/mutation";
import { format } from "date-fns";
import { RotateCwIcon } from "lucide-react";
import { mutateRequest, putFetcherKeyed } from "@/lib/swr";
import { Button } from "@/components/ui/button";
import { MarkdownTextarea } from "@/components/markdown-textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProseMarkdown } from "@/components/prose-markdown";
import type { Prd } from "@/types/domain";
import { DATE_TIME } from "@/lib/date";

const ActionButton = (props: ComponentProps<typeof Button>) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="h-7 gap-1.5 px-2 text-xs"
    {...props}
  />
);

// PRD document body (markdown).
// - Feishu PRDs: Sync pulls the doc into the DB; Edit fixes conversion
//   glitches before agents read it (agents prefer this cached copy).
// - Native PRDs (no docUrl): the body is authored right here — Write/Preview
//   tabs, GitHub-issue style.
// No "Document" heading and no local action row (owner 2026-08-18) — the
// body speaks for itself; the Edit/Write entry lives in the page header's
// ⋯ menu (editing state is lifted to Detail), Sync sits next to Follow.
// Save/Cancel surface as real buttons only while editing.
export const PrdContentSection = ({
  prd,
  editing,
  onEditingChange,
}: {
  prd: Prd;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
}) => {
  const isNative = !prd.docUrl;
  const [tab, setTab] = useState<"write" | "preview">("write");
  const [draft, setDraft] = useState("");

  // Seed the draft when edit mode turns on (rising edge only — a background
  // content sync mid-edit must not clobber the draft).
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing && !wasEditing.current) {
      setDraft(prd.content ?? "");
      setTab("write");
    }
    wasEditing.current = editing;
  }, [editing, prd.content]);

  const { trigger: save, isMutating: saving } = useSWRMutation(
    `/prds/${prd.id}#content`,
    putFetcherKeyed,
    {
      onSuccess: () => {
        mutateRequest(`/prds/${prd.id}`);
        onEditingChange(false);
      },
    }
  );

  const content: string = prd.content ?? "";
  const syncedAt = prd.contentSyncedAt
    ? format(new Date(prd.contentSyncedAt), DATE_TIME)
    : null;

  return (
    // The detail layout provides the spacing above the document.
    <div>
      {editing ? (
        <div className="mb-2 flex items-center justify-end gap-2">
          <ActionButton
            onClick={() => save({ content: draft })}
            disabled={saving}
          >
            Save
          </ActionButton>
          <ActionButton onClick={() => onEditingChange(false)} disabled={saving}>
            Cancel
          </ActionButton>
        </div>
      ) : syncedAt && !isNative ? (
        <p className="mb-2 text-xs text-muted-foreground">Synced {syncedAt}</p>
      ) : null}
      {editing ? (
        <div className="flex flex-col gap-2">
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as "write" | "preview")}
          >
            <TabsList>
              <TabsTrigger value="write">
                Write
              </TabsTrigger>
              <TabsTrigger value="preview">
                Preview
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === "write" ? (
            <MarkdownTextarea
              value={draft}
              onValueChange={setDraft}
              rows={24}
              placeholder="Write the PRD in markdown… (paste/drop images to upload)"
              className="font-mono text-xs leading-relaxed"
            />
          ) : (
            <div className="min-h-[200px] rounded-md border p-3">
              {draft.trim() ? (
                <ProseMarkdown content={draft} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nothing to preview.
                </p>
              )}
            </div>
          )}
        </div>
      ) : content ? (
        <ProseMarkdown content={content} />
      ) : isNative ? (
        <p className="text-sm text-muted-foreground">
          No content yet. Click Write to draft the PRD in markdown.
        </p>
      ) : (
        // Feishu PRD with no body yet: the server syncs it in the background
        // (kicked off on creation and again whenever this page is opened) and
        // the detail query polls until it lands.
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <RotateCwIcon className="h-3.5 w-3.5 animate-spin" />
          Syncing document from Feishu… If it never appears, click Sync in the
          title row to authorize access.
        </p>
      )}
    </div>
  );
};
